import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { IConfig, IDynamicElement, ImageData, ImageDimensions, ImageItemComponent, MediaImage } from '@sirius/common';

export interface ILogoCarouselItem {
    image: MediaImage;
    alt?: string | null;
    width?: number | null;
    height?: number | null;
}

export interface ILogoCarousel extends IConfig {
    /** Backend `string? Heading` — absent or null when the editor left it blank. */
    heading?: string | null;
    logos: ILogoCarouselItem[];
}

export const MAX_LOGO_HEIGHT = 150;

export interface LogoCarouselLogoView {
    image: MediaImage;
    alt: string;
    width: number | null;
    height: number | null;
    imageData: ImageData | null;
}

// The track is the logo set repeated this many times, animated left by exactly one set width
// (100% / SET_COPIES) so the loop point is invisible. Two copies only stay seamless while one set is
// wider than the viewport; with fewer or smaller logos a blank region scrolls in and the snap back to
// the start shows. Four keeps the track at least viewport + one set wide for rosters down to roughly a
// third of the viewport width.
const SET_COPIES = 4;

@Component({
    selector: 'ur-logo-carousel',
    templateUrl: './logo-carousel.component.html',
    styleUrl: './logo-carousel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ImageItemComponent, NgTemplateOutlet],
})
export class LogoCarouselComponent implements IDynamicElement<ILogoCarousel> {
    readonly config = input<ILogoCarousel>();

    readonly heading = computed(() => this.config()?.heading ?? '');

    readonly logos = computed<LogoCarouselLogoView[]>(() =>
        (this.config()?.logos ?? [])
            .filter((logo) => !!logo.image?.image?.url)
            .map((logo) => {
                const box = toDisplayBox(logo.width, logo.height);

                return {
                    image: logo.image,
                    alt: logo.alt ?? '',
                    width: box?.w ?? null,
                    height: box?.h ?? null,
                    imageData: box ? new ImageData('logo-carousel-logo', [box, box, box, box, box]) : null,
                };
            })
    );

    readonly maxLogoHeight = MAX_LOGO_HEIGHT;

    /** Indices for the `aria-hidden` repeats of the set — every copy after the first, real one. */
    readonly duplicateSets = Array.from({ length: SET_COPIES - 1 }, (_, index) => index);

    /** One set width as a percentage of the track, for the marquee keyframe to translate by. */
    readonly trackShift = `-${100 / SET_COPIES}%`;
}

function toDisplayBox(width?: number | null, height?: number | null): ImageDimensions | null {
    if (!width || !height || width <= 0 || height <= 0) {
        return null;
    }

    const scale = Math.min(1, MAX_LOGO_HEIGHT / height);

    return new ImageDimensions(Math.max(1, Math.round(width * scale)), Math.max(1, Math.round(height * scale)));
}
